import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  rolesApi,
  UserWithRole,
  RoleInfo,
  CreateInternalUserData,
  InviteWithMeta,
  CreateInviteData,
} from '../../../api/services/roles.service';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { selectUser } from '@entities/auth/model';
import {
  setErrorNotification,
  setNotification,
} from '@app/store/notifications';
import { companiesApi } from '../../../api/services/companies.service';
import Pagination from '@shared/ui/pagination/Pagination';
import { DEFAULT_PAGE_SIZE } from '@shared/config/pagination';
import { ConfirmModal } from '@shared/ui/confirm-modal';
import {
  INVITE_STATUS_STYLE,
  buildInviteLink,
  copyToClipboard,
} from '@entities/invite/lib/invite-link';

const PAGE_SIZE = DEFAULT_PAGE_SIZE;

/* ─────────────────────── scope ─────────────────────── */

type Scope = 'admin' | 'company' | 'hd' | 'none';

// Группа-«оунер» исторически называется и OWNER, и COMPANY_OWNER —
// разные пути создания юзера. Обе считаем владельцем компании.
// Каноническое имя группы-«владельца» — COMPANY_OWNER. Дубликат OWNER
// был выпилен миграцией.
const OWNER_GROUP_NAMES = ['COMPANY_OWNER'];

// Повторяет бэковый _get_scope: is_superuser / admins — admin;
// OWNER (любой из вариантов) + привязанная company — company; иначе none.
function resolveScope(me: any): Scope {
  if (!me) return 'none';
  if (me.is_superuser) return 'admin';
  const groups: string[] = me.groups || [];
  if (groups.includes('admins')) return 'admin';
  if (me.company && groups.some((g) => OWNER_GROUP_NAMES.includes(g))) {
    return 'company';
  }
  // Руководитель ТП (HD_HEAD / hd_lead_operator): право hd_manage_operators
  // даёт ограниченный scope — только сотрудники техподдержки и HD-роли
  // (зеркалит scope 'hd' в views/roles.py).
  const ticketPerms: string[] = me.permissions?.ticket || [];
  if (ticketPerms.includes('hd_manage_operators')) return 'hd';
  return 'none';
}

/* ─────────────────────── утилиты ─────────────────────── */

const ROLE_COLORS: Record<string, string> = {
  ADMIN: 'bg-purple-100 text-purple-700',
  MANAGER: 'bg-blue-100 text-blue-700',
  OFFICE_MANAGER: 'bg-cyan-100 text-cyan-700',
  SALES_DIRECTOR: 'bg-indigo-100 text-indigo-700',
  HD_HEAD: 'bg-red-100 text-red-600',
  HD_ENGINEER: 'bg-orange-100 text-orange-700',
  HD_SERVICE_ENGINEER: 'bg-amber-100 text-amber-700',
  OWNER: 'bg-green-100 text-green-700',
  USER: 'bg-emerald-100 text-emerald-700',
  COMPANY_ENGINEER: 'bg-teal-100 text-teal-700',
  BASIC: 'bg-gray-100 text-gray-600',
};

const roleBadge = (role: string, label: string) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      ROLE_COLORS[role] || 'bg-gray-100 text-gray-600'
    }`}
  >
    {label}
  </span>
);

// INVITE_STATUS_STYLE вынесен в @entities/invite/lib/invite-link
// и переиспользуется на карточке компании.

const INVITE_TYPE_LABEL: Record<string, string> = {
  employee: 'Сотрудник',
  company: 'Компания',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractApiMessage(err: any, fallback: string): string {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (typeof data.detail === 'string') return data.detail;
  for (const k of Object.keys(data)) {
    const v = data[k];
    if (typeof v === 'string') return v;
    if (Array.isArray(v) && typeof v[0] === 'string') return v[0];
  }
  return fallback;
}

function personName(u: UserWithRole) {
  return [u.last_name, u.first_name].filter(Boolean).join(' ') || u.username;
}

function initials(u: UserWithRole) {
  const f = (u.first_name || '').charAt(0);
  const l = (u.last_name || '').charAt(0);
  return (f + l).toUpperCase() || '?';
}

// getBaseUrl / copyToClipboard вынесены в
// @entities/invite/lib/invite-link и переиспользуются.

/* ─────────────────────── модалка создания внутреннего ─────────────────────── */

interface CreateUserModalProps {
  roles: RoleInfo[];
  onClose: () => void;
  onSave: (data: CreateInternalUserData) => Promise<void>;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({
  roles,
  onClose,
  onSave,
}) => {
  const internalRoles = roles.filter(
    (r) => r.is_internal && r.code !== 'ADMIN'
  );
  const [form, setForm] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    patronymic: '',
    password: '',
    role: internalRoles[0]?.code || 'MANAGER',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !form.username.trim() ||
      !form.email.trim() ||
      !form.password.trim()
    ) {
      setError('Заполните обязательные поля');
      return;
    }
    if (form.password.length < 6) {
      setError('Пароль должен быть не короче 6 символов');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  const field = (
    label: string,
    key: keyof typeof form,
    type = 'text',
    required = false
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-[#2d2c2c]">
            Новый сотрудник
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {field('Фамилия', 'last_name')}
            {field('Имя', 'first_name')}
          </div>
          {field('Отчество', 'patronymic')}
          <div className="grid grid-cols-2 gap-4">
            {field('Логин', 'username', 'text', true)}
            {field('Email', 'email', 'email', true)}
          </div>
          {field('Пароль', 'password', 'password', true)}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Роль<span className="text-red-500">*</span>
            </label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
            >
              {internalRoles.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] disabled:opacity-50"
            >
              {saving ? 'Создаём…' : 'Создать'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─────────────────────── модалка создания инвайта ─────────────────────── */

interface CreateInviteModalProps {
  assignableRoles: RoleInfo[]; // внешние роли для EMPLOYEE
  /** Список компаний для выбора. Если пустой/undefined — селекта нет. */
  companies?: { id: number; name: string }[];
  /** Если true — компанию выбрать обязательно (для админа). */
  requireCompany?: boolean;
  onClose: () => void;
  onSave: (data: CreateInviteData) => Promise<void>;
}

const CreateInviteModal: React.FC<CreateInviteModalProps> = ({
  assignableRoles,
  companies = [],
  requireCompany = false,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    target_role: assignableRoles[0]?.code || 'USER',
    target_company: '' as number | '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = [form.last_name, form.first_name]
      .map((s) => s.trim())
      .filter(Boolean)
      .join(' ');
    if (!name) {
      setError('Укажите имя и фамилию');
      return;
    }
    if (requireCompany && !form.target_company) {
      setError('Выберите компанию');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await onSave({
        name,
        type: 'employee',
        target_role: form.target_role,
        target_company:
          form.target_company === '' ? undefined : Number(form.target_company),
        // first_name/last_name летят отдельно: бэк (RolesViewSet
        // .create_invite_action) кладёт их в Invite.partial_data
        // вместе с target_role, и страница регистрации по инвайту
        // подставляет в форму. Без этого поля «Имя/Фамилия» на
        // первом шаге регистрации оставались пустыми — это и был
        // баг «при регистрации по приглашению не подставляется имя».
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-[#2d2c2c]">
            Пригласить в компанию
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Фамилия
              </label>
              <input
                value={form.last_name}
                onChange={(e) =>
                  setForm({ ...form, last_name: e.target.value })
                }
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Имя
              </label>
              <input
                value={form.first_name}
                onChange={(e) =>
                  setForm({ ...form, first_name: e.target.value })
                }
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Роль при регистрации
            </label>
            <select
              value={form.target_role}
              onChange={(e) =>
                setForm({ ...form, target_role: e.target.value })
              }
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
            >
              {assignableRoles.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {companies.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Компания
                {requireCompany && <span className="text-red-500">*</span>}
              </label>
              <select
                value={form.target_company}
                onChange={(e) =>
                  setForm({
                    ...form,
                    target_company:
                      e.target.value === '' ? '' : Number(e.target.value),
                  })
                }
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
              >
                <option value="">— выберите компанию —</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] disabled:opacity-50"
            >
              {saving ? 'Создаём…' : 'Создать приглашение'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─────────────────────── модалка смены роли ─────────────────────── */

interface ChangeRoleModalProps {
  user: UserWithRole;
  roles: RoleInfo[];
  onClose: () => void;
  onSave: (userId: number, role: string) => Promise<void>;
}

const ChangeRoleModal: React.FC<ChangeRoleModalProps> = ({
  user,
  roles,
  onClose,
  onSave,
}) => {
  const [selectedRole, setSelectedRole] = useState(user.role);
  const [saving, setSaving] = useState(false);
  const assignableRoles = roles.filter((r) => r.code !== 'ADMIN');

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(user.id, selectedRole);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-[#2d2c2c]">Сменить роль</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Пользователь:{' '}
            <span className="font-semibold text-[#2d2c2c]">
              {user.last_name} {user.first_name}
            </span>{' '}
            ({user.email})
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Внутренние роли
            </label>
            <div className="space-y-1.5">
              {assignableRoles
                .filter((r) => r.is_internal)
                .map((r) => (
                  <label
                    key={r.code}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="role"
                      value={r.code}
                      checked={selectedRole === r.code}
                      onChange={() => setSelectedRole(r.code)}
                    />
                    <span className="text-sm">{r.label}</span>
                  </label>
                ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Внешние роли
            </label>
            <div className="space-y-1.5">
              {assignableRoles
                .filter((r) => !r.is_internal)
                .map((r) => (
                  <label
                    key={r.code}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="role"
                      value={r.code}
                      checked={selectedRole === r.code}
                      onChange={() => setSelectedRole(r.code)}
                    />
                    <span className="text-sm">{r.label}</span>
                  </label>
                ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              Отмена
            </button>
            <button
              onClick={handleSave}
              disabled={saving || selectedRole === user.role}
              className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] disabled:opacity-40"
            >
              {saving ? 'Сохраняем…' : 'Сохранить'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────── инвайт-строка ─────────────────────── */

interface InviteRowProps {
  invite: InviteWithMeta;
  canRevoke: boolean;
  onRevoke: (id: string) => void;
}

const InviteRow: React.FC<InviteRowProps> = ({ invite, canRevoke, onRevoke }) => {
  const dispatch = useAppDispatch();
  const statusStyle =
    INVITE_STATUS_STYLE[invite.status] || INVITE_STATUS_STYLE.unused;
  const inviteLink = buildInviteLink(invite.id);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const ok = await copyToClipboard(inviteLink);
    dispatch(
      ok
        ? setNotification('Ссылка скопирована в буфер обмена')
        : setErrorNotification('Не удалось скопировать ссылку')
    );
  };

  return (
    <tr className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50">
      <td className="py-3 px-2">
        <p className="font-medium text-[#2d2c2c]">{invite.name}</p>
        <p className="text-xs text-gray-400">
          {INVITE_TYPE_LABEL[invite.type] || invite.type}
          {invite.target_role_label ? ` · ${invite.target_role_label}` : ''}
        </p>
      </td>
      <td className="py-3 px-2">
        <span
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
          style={{ background: statusStyle.bg, color: statusStyle.fg }}
        >
          {statusStyle.label}
        </span>
      </td>
      <td className="py-3 px-2 text-xs text-gray-500">
        {invite.created_at
          ? new Date(invite.created_at).toLocaleString('ru-RU')
          : '—'}
      </td>
      <td className="py-3 px-2 text-right">
        {invite.status === 'unused' && (
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={handleCopy}
              className="text-sm font-medium text-gray-600 hover:text-[#e3282a] transition-colors"
              type="button"
            >
              Скопировать
            </button>
            {canRevoke && (
              <ConfirmModal
                danger
                openButton={
                  <button
                    type="button"
                    className="text-sm font-medium text-[#e3282a] hover:text-[#c82022] transition-colors"
                  >
                    Отозвать
                  </button>
                }
                confirm={() => onRevoke(invite.id)}
                texts={{
                  title: 'Отозвать приглашение?',
                  text: 'После этого ссылка перестанет работать. Получивший её человек не сможет зарегистрироваться.',
                  cancel: 'Отмена',
                  confirm: 'Отозвать',
                }}
              />
            )}
          </div>
        )}
      </td>
    </tr>
  );
};

/* ─────────────────────── основной экран ─────────────────────── */

interface CompanyOption {
  id: number;
  name: string;
}

const UsersRolesSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const me = useAppSelector(selectUser);
  const scope = useMemo<Scope>(() => resolveScope(me), [me]);

  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [invites, setInvites] = useState<InviteWithMeta[]>([]);
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [companiesList, setCompaniesList] = useState<CompanyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState<number | ''>('');
  const [activeTab, setActiveTab] = useState<'users' | 'invites'>('users');
  const [usersPage, setUsersPage] = useState(1);
  const [invitesPage, setInvitesPage] = useState(1);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showCreateInvite, setShowCreateInvite] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithRole | null>(null);

  const isAdmin = scope === 'admin';
  const isOwner = scope === 'company';
  const isHdHead = scope === 'hd';
  // Кто может заводить внутренних сотрудников и менять им роль:
  // админ — любые роли, руководитель ТП — только HD-роли (список ролей
  // для него уже отфильтрован бэком в /roles/available/).
  const canManageInternal = isAdmin || isHdHead;
  const hasAccess = isAdmin || isOwner || isHdHead;

  const loadUsers = useCallback(async () => {
    if (!hasAccess) return;
    setLoading(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const params: any = {};
      if (roleFilter) params.role = roleFilter;
      if (search.trim()) params.search = search.trim();
      if (companyFilter) params.company = companyFilter;
      const res = await rolesApi.getUsers(params);
      setUsers(res.data);
    } catch (err) {
      dispatch(
        setErrorNotification(
          extractApiMessage(err, 'Не удалось загрузить пользователей')
        )
      );
    } finally {
      setLoading(false);
    }
  }, [hasAccess, roleFilter, search, companyFilter, dispatch]);

  const loadInvites = useCallback(async () => {
    if (!hasAccess) return;
    try {
      const res = await rolesApi.getInvites();
      setInvites(res.data);
    } catch (err) {
      dispatch(
        setErrorNotification(
          extractApiMessage(err, 'Не удалось загрузить приглашения')
        )
      );
    }
  }, [hasAccess, dispatch]);

  useEffect(() => {
    if (!hasAccess) return;
    rolesApi
      .getAvailableRoles()
      .then((res) => setRoles(res.data))
      .catch(() => {});
  }, [hasAccess]);

  // Список компаний для админа — нужен и в селекте фильтра,
  // и в селекте «к какой компании привязать инвайт».
  useEffect(() => {
    if (!isAdmin) return;
    companiesApi
      .getAllCompanies()
      .then((res) =>
        setCompaniesList(
          (res.data || []).map((c) => ({ id: c.id, name: c.name }))
        )
      )
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // При изменении фильтров возвращаем пользователя на 1-ю страницу,
  // чтобы он не оставался на странице, которой больше нет.
  useEffect(() => {
    setUsersPage(1);
  }, [search, roleFilter, companyFilter]);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  const handleCreateUser = useCallback(
    async (data: CreateInternalUserData) => {
      try {
        await rolesApi.createInternal(data);
        dispatch(setNotification('Пользователь создан'));
        setShowCreateUser(false);
        loadUsers();
      } catch (err) {
        dispatch(
          setErrorNotification(
            extractApiMessage(err, 'Не удалось создать пользователя')
          )
        );
      }
    },
    [dispatch, loadUsers]
  );

  const handleChangeRole = useCallback(
    async (userId: number, role: string) => {
      try {
        await rolesApi.changeRole(userId, role);
        dispatch(setNotification('Роль изменена'));
        setEditingUser(null);
        loadUsers();
      } catch (err) {
        dispatch(
          setErrorNotification(
            extractApiMessage(err, 'Не удалось сменить роль')
          )
        );
      }
    },
    [dispatch, loadUsers]
  );

  const handleCreateInvite = useCallback(
    async (data: CreateInviteData) => {
      try {
        const res = await rolesApi.createInvite(data);
        // Сразу копируем ссылку в буфер — самый частый следующий шаг.
        const link = buildInviteLink(res.data.id);
        const copied = await copyToClipboard(link);
        // Один тост вместо двух подряд (раньше «Создано» сразу
        // перетиралось «Скопировано», и юзер успевал увидеть только
        // второе сообщение).
        dispatch(
          setNotification(
            copied
              ? 'Приглашение создано — ссылка скопирована'
              : 'Приглашение создано'
          )
        );
        setShowCreateInvite(false);
        setActiveTab('invites');
        loadInvites();
      } catch (err) {
        dispatch(
          setErrorNotification(
            extractApiMessage(err, 'Не удалось создать приглашение')
          )
        );
      }
    },
    [dispatch, loadInvites]
  );

  const handleRevokeInvite = useCallback(
    async (inviteId: string) => {
      try {
        await rolesApi.revokeInvite(inviteId);
        dispatch(setNotification('Приглашение отозвано'));
        loadInvites();
      } catch (err) {
        dispatch(
          setErrorNotification(
            extractApiMessage(err, 'Не удалось отозвать приглашение')
          )
        );
      }
    },
    [dispatch, loadInvites]
  );

  if (!hasAccess) {
    return (
      <div className="bg-white rounded-xl p-8 shadow-sm text-center">
        <h2 className="text-lg font-semibold text-[#2d2c2c] mb-2">
          Раздел недоступен
        </h2>
        <p className="text-sm text-gray-500">
          Управление пользователями доступно администраторам и владельцам
          компании.
        </p>
      </div>
    );
  }

  const assignableInviteRoles = roles.filter(
    (r) => !r.is_internal && ['USER', 'COMPANY_OWNER'].includes(r.code)
  );

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-sm">
        {/* Заголовок «Пользователи и роли» вынесен в шапку приложения,
            оставляем только подпись с описанием режима и CTA-кнопки. */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-gray-500">
              {isAdmin
                ? 'Управление ролями внутренних и внешних пользователей'
                : isHdHead
                  ? 'Сотрудники технической поддержки'
                  : 'Управление пользователями вашей компании'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Бэк разрешает EMPLOYEE-инвайт и админу (с выбором компании),
                модалка это поддерживает — раньше кнопка была скрыта. */}
            {(isOwner || isAdmin) && (
              <button
                onClick={() => setShowCreateInvite(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] transition-colors"
              >
                + Пригласить
              </button>
            )}
            {canManageInternal && (
              <button
                onClick={() => setShowCreateUser(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] transition-colors"
              >
                + Новый сотрудник
              </button>
            )}
          </div>
        </div>

        {/* Вкладки */}
        <div className="flex items-center gap-6 border-b border-gray-200 mb-4">
          <button
            onClick={() => setActiveTab('users')}
            className={`pb-3 text-sm font-medium transition-colors ${
              activeTab === 'users'
                ? 'text-[#e3282a] border-b-2 border-[#e3282a]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            type="button"
          >
            Пользователи
            <span className="ml-1.5 text-xs text-gray-400">({users.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('invites')}
            className={`pb-3 text-sm font-medium transition-colors ${
              activeTab === 'invites'
                ? 'text-[#e3282a] border-b-2 border-[#e3282a]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            type="button"
          >
            Приглашения
            <span className="ml-1.5 text-xs text-gray-400">
              ({invites.length})
            </span>
          </button>
        </div>

        {activeTab === 'users' && (
          <>
            {/* Фильтры */}
            <div className="flex items-center gap-3 mb-4">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск по имени или email..."
                className="flex-1 max-w-[320px] px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
              />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
              >
                <option value="">Все роли</option>
                {/* Админ видит все роли (включая ADMIN), OWNER —
                    только тот набор, который ему вернул /roles/available/
                    (внешние роли его компании). */}
                {isAdmin && <option value="ADMIN">Администратор</option>}
                {roles
                  .filter((r) => r.code !== 'ADMIN')
                  .map((r) => (
                    <option key={r.code} value={r.code}>
                      {r.label}
                    </option>
                  ))}
              </select>
              {isAdmin && (
                <select
                  value={companyFilter}
                  onChange={(e) =>
                    setCompanyFilter(
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400 max-w-[260px]"
                >
                  <option value="">Все компании</option>
                  {companiesList.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
              {(search.trim() || roleFilter || companyFilter !== '') && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setRoleFilter('');
                    setCompanyFilter('');
                  }}
                  className="text-sm font-medium text-gray-500 hover:text-[#e3282a] transition-colors"
                >
                  Сбросить
                </button>
              )}
            </div>

            {loading ? (
              <div className="animate-pulse space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-14 bg-gray-100 rounded-lg" />
                ))}
              </div>
            ) : users.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">
                Нет пользователей по заданным критериям.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Пользователь
                      </th>
                      <th className="text-left py-3 px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="text-left py-3 px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Роль
                      </th>
                      {isAdmin && (
                        <th className="text-left py-3 px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          Компания
                        </th>
                      )}
                      <th className="text-right py-3 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users
                      .slice(
                        (usersPage - 1) * PAGE_SIZE,
                        usersPage * PAGE_SIZE
                      )
                      .map((u) => {
                      const avatarColors = [
                        'bg-red-100 text-red-600',
                        'bg-blue-100 text-blue-600',
                        'bg-green-100 text-green-600',
                        'bg-purple-100 text-purple-600',
                      ];
                      const color = avatarColors[u.id % avatarColors.length];

                      return (
                        <tr
                          key={u.id}
                          className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50"
                        >
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold ${color}`}
                              >
                                {initials(u)}
                              </div>
                              <div>
                                <p className="font-medium text-[#2d2c2c]">
                                  {personName(u)}
                                </p>
                                <p className="text-xs text-gray-400">
                                  @{u.username}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-gray-600">{u.email}</td>
                          <td className="py-3 px-2">
                            {u.is_superuser
                              ? roleBadge('ADMIN', 'Администратор')
                              : roleBadge(u.role, u.role_label)}
                          </td>
                          {isAdmin && (
                            <td className="py-3 px-2 text-gray-500">
                              {u.company_name || '—'}
                            </td>
                          )}
                          <td className="py-3 px-2 text-right">
                            {canManageInternal && !u.is_superuser && u.id !== me?.id && (
                              <button
                                onClick={() => setEditingUser(u)}
                                className="text-sm font-medium text-[#e3282a] hover:text-[#c82022] transition-colors"
                              >
                                Изменить
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <Pagination
                  currentPage={usersPage}
                  totalPages={Math.max(1, Math.ceil(users.length / PAGE_SIZE))}
                  totalItems={users.length}
                  pageSize={PAGE_SIZE}
                  onPageChange={setUsersPage}
                />
              </div>
            )}
          </>
        )}

        {activeTab === 'invites' && (
          <>
            {invites.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">
                Приглашений пока нет.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Кого приглашают
                      </th>
                      <th className="text-left py-3 px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Статус
                      </th>
                      <th className="text-left py-3 px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Создан
                      </th>
                      <th className="text-right py-3 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invites
                      .slice(
                        (invitesPage - 1) * PAGE_SIZE,
                        invitesPage * PAGE_SIZE
                      )
                      .map((invite) => (
                        <InviteRow
                          key={invite.id}
                          invite={invite}
                          canRevoke={invite.status === 'unused'}
                          onRevoke={handleRevokeInvite}
                        />
                      ))}
                  </tbody>
                </table>
                <Pagination
                  currentPage={invitesPage}
                  totalPages={Math.max(
                    1,
                    Math.ceil(invites.length / PAGE_SIZE)
                  )}
                  totalItems={invites.length}
                  pageSize={PAGE_SIZE}
                  onPageChange={setInvitesPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Модалки */}
      {showCreateUser && (
        <CreateUserModal
          roles={roles}
          onClose={() => setShowCreateUser(false)}
          onSave={handleCreateUser}
        />
      )}
      {showCreateInvite && (
        <CreateInviteModal
          assignableRoles={assignableInviteRoles}
          companies={isAdmin ? companiesList : []}
          requireCompany={isAdmin}
          onClose={() => setShowCreateInvite(false)}
          onSave={handleCreateInvite}
        />
      )}
      {editingUser && (
        <ChangeRoleModal
          user={editingUser}
          roles={roles}
          onClose={() => setEditingUser(null)}
          onSave={handleChangeRole}
        />
      )}
    </div>
  );
};

export default UsersRolesSettings;
